import React, { useState, useEffect } from 'react';
import Sparkles from '../../../macadamia-core/icons/Sparkles';

// CSS pour les différents états de l'input
const GlowCSS = `
  .search-glow-wrapper {
    position: relative;
    border-radius: 8px;
    overflow: visible;
  }

  /* Effet de bordure interne (pour la recherche) */
  .search-glow-border {
    position: absolute;
    inset: -2px;
    border-radius: 10px;
    z-index: 0;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    background: linear-gradient(90deg, 
      #FF6778, #FFBA71, #8D99FF, #F5B9EA, #FF6778
    );
    background-size: 300% 100%;
    animation: gradientFlow 8s ease infinite;
  }

  /* Effet de flou de fond (ombre portée) */
  .search-glow-blur {
    position: absolute;
    inset: 0px;
    border-radius: 0px;
    z-index: 1;
    opacity: 0;
    filter: blur(6px);
    transition: opacity 0.4s ease-in-out;
    background: linear-gradient(90deg, 
      #FF6778, #FFBA71, #8D99FF, #F5B9EA, #FF6778
    );
    background-size: 300% 100%;
    animation: gradientFlow 8s ease infinite;
  }

  @keyframes gradientFlow {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  /* État focused : activation du flou de fond */
  .search-glow-wrapper-focused .search-glow-blur {
    opacity: 0.7;
  }

  /* État avec texte : intensification légère du flou */
  .search-glow-wrapper-with-text .search-glow-blur {
    opacity: 0.9;
  }

  /* État recherche : activation de la bordure interne */
  .search-glow-wrapper-searching .search-glow-border {
    opacity: 1;
  }

  .search-glow-inner {
    position: relative;
    border-radius: 8px;
    z-index: 1;
    transition: all 0.3s ease;
  }
`;

export interface HALSearchQuery {
  value: string;
  field: 'q' | 'title' | 'author' | 'abstract' | 'keyword';
  label?: string; // Pour l'affichage (ex: "Auteurs : Jean")
}

interface HALSearchInputProps {
  onSearch: (query: HALSearchQuery) => void;
  initialValue?: string;
}

const HALSearchInput: React.FC<HALSearchInputProps> = ({ onSearch, initialValue = '' }) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const suggestions = [
    { field: 'q', label: `Rechercher partout : "${searchTerm}"` },
    { field: 'title', label: `Titre : "${searchTerm}"` },
    { field: 'author', label: `Auteurs : "${searchTerm}"` },
    { field: 'abstract', label: `Résumé : "${searchTerm}"` },
    { field: 'keyword', label: `Mots-clés : "${searchTerm}"` }
  ];

  // Injecter le CSS dans le document
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = GlowCSS;
    document.head.appendChild(styleElement);

    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  // Gérer le focus
  const handleFocus = () => {
    setIsFocused(true);
    if (searchTerm.trim()) setShowSuggestions(true);
  };

  // Gérer la perte du focus (délai pour permettre le clic)
  const handleBlur = () => {
    // Petit délai pour permettre le clic sur la suggestion avant que le dropdown disparaisse
    setTimeout(() => {
      setIsFocused(false);
      setShowSuggestions(false);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (searchTerm.trim()) {
        handleSuggestionClick(suggestions[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(value.trim().length > 0);
    setSelectedIndex(0); // Reset selection
  };

  const handleSuggestionClick = (suggestion: any) => {
    onSearch({
      value: searchTerm,
      field: suggestion.field as any,
      label: suggestion.label
    });
    setShowSuggestions(false);
  };

  // Déterminer les classes CSS pour l'état de l'input
  const getWrapperClasses = () => {
    const classes = ['search-glow-wrapper'];

    if (searchTerm.trim()) {
      classes.push('search-glow-wrapper-with-text');
    }
    if (isFocused || showSuggestions) {
      classes.push('search-glow-wrapper-focused');
    }

    return classes.join(' ');
  };

  return (
    <div className={getWrapperClasses()}>
      {/* Effet de bordure interne (recherche) */}
      <div className="search-glow-border"></div>

      {/* Effet de flou de fond (focus et texte) */}
      <div className="search-glow-blur"></div>

      {/* Conteneur interne */}
      <div className="search-glow-inner relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Sparkles
            width="20px"
            height="20px"
            stroke="rgb(156 163 175)"
          />
        </div>
        <input
          type="text"
          placeholder="Rechercher par titre, auteur, mots-clés..."
          value={searchTerm}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 
                     bg-white dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100
                     focus:ring-2 focus:ring-mcdm-primary-300 dark:focus:ring-mcdm-third-200
                     focus:border-mcdm-primary-300 outline-none transition-all duration-200
                     placeholder-gray-400 dark:placeholder-gray-500 text-sm"
        />

        {/* Dropdown de suggestions */}
        {showSuggestions && searchTerm.trim().length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-mcdm-third-300 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-50 overflow-hidden text-sm">
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.field}
                className={`px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors duration-150 ${index === selectedIndex ? 'bg-mcdm-primary-50 dark:bg-mcdm-third-200 text-mcdm-secondary-400' : 'hover:bg-gray-50 dark:hover:bg-mcdm-third-200 text-gray-700 dark:text-gray-200'}`}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <span className={`w-2 h-2 rounded-full ${index === selectedIndex ? 'bg-mcdm-secondary-400' : 'bg-transparent'}`}></span>
                {suggestion.field === 'q' ? (
                  <span className="font-medium italic">Rechercher partout : "{searchTerm}"</span>
                ) : (
                  <span>
                    <span className="font-semibold text-mcdm-primary-400 capitalize">{suggestion.field === 'title' ? 'Titre' : suggestion.field === 'author' ? 'Auteur' : suggestion.field === 'keyword' ? 'Mot-clé' : 'Résumé'}</span> : "{searchTerm}"
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HALSearchInput;
